import classNames from 'classnames'
import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import ModalComponent from '../..'
import { deepCopy, removeElementAtIndex } from '../../../../helpers'
import { useModalControl } from '../../../../hooks/useModalControl'
import { useFetchAllListArticlesQuery } from '../../../../redux-toolkits/lists/list.slice'
import {
  useCreateAffiliateMutation,
  useDeleteAffiliateListMutation,
  useGetAffiliateQuery,
  useUpdateAffiliateMutation,
} from '../../../../redux-toolkits/loyalty/loyalty.slice'
import { useGetAllCustomersQuery } from '../../../../redux-toolkits/user/user.slice'
import { CustomersQuery } from '../../../../redux-toolkits/customers/customers.type'
import { QueryProps } from '../../../../redux-toolkits/shared/shared.type'
import { AddAffiliateSchema } from '../../../../validation/validate'
import { AccountDetails } from '../../../accountDetails'
import { Button } from '../../../button'
import Icon from '../../../icon'
import ComboBox from '../../../inputs/combobox'
import Spinner from '../../../spinner/Spinner'
import { ConfirmationModal } from '../confirmationModal'

type T = {
  id?: string
  name?: string
}

export const AddAffiliateModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: () => void
  affiliateId?: string
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  affiliateId,
}) => {
  const [query, setQuery] = useState<CustomersQuery>({
    skip: 0,
    limit: 10,
    search: undefined,
    deleted: undefined,
    startDate: undefined,
    endDate: undefined,
    isCustomDateRange: false,
    filter: 'inception_to_date',
    sort: 'fully_onboarded',
  })
  const [listQuery, setListQuery] = useState<QueryProps>({
    page: 1,
    limit: 20,
    search: undefined,
  })
  const [selecetdListId, setSelectedListId] = useState<string>()

  const { modal, modalOpen, handleModalClose, handleModalOpen, setModalOpen } =
    useModalControl()

  const [createAffiliate, { isLoading: creatingAffiliate }] =
    useCreateAffiliateMutation()

  const [updateAffiliate, { isLoading: updatingAffiliate }] =
    useUpdateAffiliateMutation()

  const [deleteAffiliateList, { isLoading: deletingAffiliateList }] =
    useDeleteAffiliateListMutation()

  const formik = useFormik({
    initialValues: {
      customer: {
        id: undefined,
        name: '',
      },
      lists: [
        {
          id: undefined,
          name: '',
        },
      ],
      accountName: '',
      accountNumber: '',
      bankCode: '',
      bankName: '',
    },
    validationSchema: AddAffiliateSchema,
    onSubmit: async (vals, { resetForm }) => {
      if (!affiliateId) {
        const resp = await createAffiliate({
          user: vals.customer.id!,
          lists: vals.lists.map((v) => v.id!),
          // settlementAccount: {
          //   accountNumber: vals.accountNumber,
          //   bankCode: vals.bankCode,
          // },
        })

        if (resp.data?.success) {
          toast.success(resp.data.message)
          resetForm()
          onSuccessCallback()
        }
      } else {
        const resp = await updateAffiliate({
          id: affiliateId,
          payload: {
            lists: vals.lists.map((v) => v.id!),
            settlementAccount: vals.accountNumber
              ? {
                  accountNumber: vals.accountNumber,
                  bankCode: vals.bankCode,
                }
              : undefined,
          },
        })

        if (resp.data?.success) {
          toast.success(resp.data.message)
          resetForm()
          onSuccessCallback()
        }
      }
    },
  })

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    setFieldValue,
    handleSubmit,
    isValid,
    resetForm,
  } = formik

  const { data: customers, isFetching: fetchingCustomers } =
    useGetAllCustomersQuery(
      { ...query, search: query.search || undefined },
      {
        refetchOnMountOrArgChange: true,
        skip: !open,
      },
    )

  const { data: lists, isFetching: fetchingLists } =
    useFetchAllListArticlesQuery(
      {
        query: {
          ...listQuery,
          search: listQuery.search || undefined,
          status: 'PUBLISHED',
        },
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !open,
      },
    )

  const { data: affiliateDetails, isFetching: fetchingAffiliateDetails } =
    useGetAffiliateQuery(
      {
        id: affiliateId!,
        refetch: open,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !affiliateId || !open,
      },
    )

  useEffect(() => {
    if (
      affiliateDetails?.data.affiliate &&
      affiliateId &&
      !fetchingAffiliateDetails
    ) {
      setFieldValue('customer', {
        id: affiliateDetails.data.affiliate.user,
        name: affiliateDetails.data.affiliate.slug,
      })
      setFieldValue(
        'accountName',
        affiliateDetails.data?.settlementAccount?.accountName,
      )
      setFieldValue(
        'accountNumber',
        affiliateDetails.data?.settlementAccount?.accountNumber,
      )
      setFieldValue(
        'bankCode',
        affiliateDetails.data?.settlementAccount?.bankCode,
      )
      setFieldValue(
        'bankName',
        affiliateDetails.data?.settlementAccount?.bankName,
      )
      setFieldValue(
        'lists',
        affiliateDetails.data?.lists?.map((l) => ({
          id: l._id,
          name: l.title,
        })),
      )
    } else {
      resetForm()
    }
  }, [affiliateDetails, affiliateId, fetchingAffiliateDetails])

  return (
    <>
      <ModalComponent
        open={open}
        setOpen={setOpen}
        onCloseCallback={() => {
          resetForm()
          onCloseCallback()
        }}
        title={
          affiliateId
            ? `Edit ${
                !fetchingAffiliateDetails
                  ? (affiliateDetails?.data?.affiliateUserData?.firstname ??
                      '') +
                    ' ' +
                    (affiliateDetails?.data?.affiliateUserData?.lastname ?? '')
                  : '...'
              }`
            : 'Add affiliate'
        }
        childrenClassName="md:!max-w-[420px]"
      >
        <form
          className="flex flex-col justify-center gap-4 relative"
          onSubmit={handleSubmit}
        >
          {fetchingAffiliateDetails ? (
            <Spinner
              fullScreen={false}
              className="absolute inset-0 bg-white/50 z-10"
            />
          ) : undefined}

          <ComboBox
            isDropdown
            type="text"
            name="customer.name"
            value={values.customer.name}
            onChange={(e) => {
              handleChange(e)
              setFieldValue('customer.id', undefined)
            }}
            onBlur={handleBlur}
            hasIcon={false}
            //   title="Affiliate"
            placeholder="Select affiliate"
            errors={
              touched.customer?.name && errors.customer?.id
                ? errors.customer.id
                : ''
            }
            menuItemsClassName=""
            menuItems={
              customers?.data?.customers?.map((cust) => ({
                name: (
                  <span>
                    <span className="capitalize">
                      {cust.firstname} {cust.lastname}
                    </span>{' '}
                    • {cust.email}
                  </span>
                ),
                value: {
                  name: `${cust.firstname} ${cust.lastname}`,
                  phone: `${cust.phoneNumber}`,
                  email: `${cust.email}`,
                  id: cust._id,
                },
              })) ?? []
            }
            onSelectOption={(selected) => {
              setFieldValue('customer', {
                id: selected?.id,
                name: selected?.name,
              })
              setQuery({
                ...query,
                search: undefined,
              })
            }}
            onSearch={(val) => {
              setQuery({
                ...query,
                search: val === '' ? undefined : val,
              })
            }}
            loading={fetchingCustomers}
            immediate
            position="bottom start"
            menuClassName="!max-h-[250px] !z-[999] shadow-md"
            className={classNames('w-full', {
              '!hidden': affiliateId,
            })}
          />

          {!affiliateId ? undefined : (
            <AccountDetails
              onChange={(details) => {
                setFieldValue('accountName', details.accountName)
                setFieldValue('accountNumber', details.accountNumber)
                setFieldValue('bankName', details.bankName)
                setFieldValue('bankCode', details.bankCode)
              }}
              formik={formik}
            />
          )}

          {values.lists.map((list, idx) => {
            return (
              <div
                key={`${list.id}-${idx}`}
                className="flex gap-3 items-center"
              >
                <ComboBox
                  isDropdown
                  type="text"
                  name={`lists[${idx}].name`}
                  value={values.lists[idx].name}
                  onChange={(e) => {
                    handleChange(e)
                    setFieldValue(`lists[${idx}].id`, undefined)
                  }}
                  onBlur={handleBlur}
                  hasIcon={false}
                  //   title={idx === 0 ? 'Affiliate lists' : undefined}
                  placeholder="Select affiliate's list"
                  errors={
                    (touched.lists ?? [])[idx]?.name &&
                    ((errors.lists ?? [])[idx] as T)?.id
                      ? ((errors.lists ?? [])[idx] as T)?.id
                      : ''
                  }
                  menuItems={
                    lists?.data?.result?.map((list) => ({
                      name: `${list.title}`,
                      value: {
                        name: `${list.title}`,
                        id: list._id,
                      },
                    })) ?? []
                  }
                  onSelectOption={(selected) => {
                    setFieldValue(`lists[${idx}]`, {
                      id: selected?.id,
                      name: selected?.name,
                    })
                    setListQuery({
                      ...listQuery,
                      search: undefined,
                    })
                  }}
                  onSearch={(val) => {
                    setListQuery({
                      ...listQuery,
                      search: val === '' ? undefined : val,
                    })
                  }}
                  loading={fetchingLists}
                  immediate
                  position="bottom start"
                  menuClassName="!max-h-[250px] !z-[999] shadow-md"
                  className="w-full"
                />

                {values.lists.length > 1 ? (
                  <button
                    className="max-w-fit h-full flex justify-self-stretch rounded-full p-1 bg-danger"
                    type="button"
                    onClick={() => {
                      if (!affiliateId) {
                        setFieldValue(
                          'lists',
                          removeElementAtIndex(deepCopy(values.lists), idx),
                        )
                      } else {
                        setSelectedListId(list.id)
                        handleModalOpen('deleteAffiliateListModal')
                      }
                    }}
                  >
                    <Icon
                      id="bin"
                      width={16}
                      height={16}
                      className="text-white"
                    />
                  </button>
                ) : undefined}
              </div>
            )
          })}

          <Button
            label="Add another list"
            type="button"
            className="!px-0 !py-0 !w-fit !max-w-fit !gap-2"
            disabled={!isValid || creatingAffiliate || updatingAffiliate}
            loading={false}
            icon="plus-circle"
            iconPosition="right"
            variant="plain"
            onClick={() => {
              setFieldValue('lists', [
                ...values.lists,
                {
                  id: undefined,
                  name: '',
                },
              ])
            }}
          />

          <Button
            label={affiliateId ? 'Save' : 'Add affiliate'}
            type="submit"
            className="mt-2 w-full"
            disabled={creatingAffiliate || updatingAffiliate}
            loading={creatingAffiliate || updatingAffiliate}
            primary
          />
        </form>
      </ModalComponent>

      <ConfirmationModal
        open={modalOpen && modal === 'deleteAffiliateListModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          setSelectedListId(undefined)
          handleModalClose()
        }}
        confirmText="Delete"
        title="Confirm Deletion"
        onConfirm={async () => {
          const resp = await deleteAffiliateList({
            affiliateId: affiliateId || '',
            listId: selecetdListId || '',
          })

          if (resp?.data?.success) {
            setSelectedListId(undefined)
            handleModalClose()
          }
        }}
        message="Are you sure you want to delete this list?"
        loading={deletingAffiliateList}
      />
    </>
  )
}
